/**
 * workspaceIndexLive — real-time workspace display names (2026-08-17 owner
 * directive: an org rename must reach every member's chrome live, on every
 * device, without a reload).
 *
 * Listens to users/{uid}/workspace_index/* — server-maintained mirror docs
 * (seeded by listMyWorkspaces on every listing, fanned out to all members by
 * renameOrganization). LABELS ONLY: membership, switching, and permissions
 * stay with the verified callables; nothing read here grants anything.
 *
 * Kept separate from workspaceOptionsCache on purpose — that module must stay
 * free of firebase imports (jsdom leaf-module rule).
 */
import { getApp } from 'firebase/app';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';

/**
 * Subscribe to live workspace names for a signed-in uid. Calls `onNames` with
 * a full accountId → displayName map on every change. Tolerance contract
 * (same as the pill's): any failure yields silence, never a throw — the
 * header must not break over identity decoration.
 */
export function subscribeWorkspaceNames(
  uid: string,
  onNames: (names: Record<string, string>) => void,
): () => void {
  try {
    const db = getFirestore(getApp());
    return onSnapshot(
      collection(db, 'users', uid, 'workspace_index'),
      (snap) => {
        const names: Record<string, string> = {};
        snap.forEach((docSnap) => {
          const d = docSnap.data() as { displayName?: unknown };
          const name = typeof d?.displayName === 'string' ? d.displayName.trim() : '';
          if (name) names[docSnap.id] = name;
        });
        onNames(names);
      },
      () => undefined,
    );
  } catch {
    return () => undefined;
  }
}
